import { useEffect, useRef } from 'react';
import { closingBehavior, mainButton, popup } from '@telegram-apps/sdk-react';
import { isMockedEnv } from './init';

function safe<T>(fn: () => T): T | undefined {
  try {
    return fn();
  } catch {
    return undefined;
  }
}

export type MainButtonOptions = {
  text: string;
  visible?: boolean;
  enabled?: boolean;
  loading?: boolean;
  onClick: () => void;
};

/**
 * Нативная MainButton Telegram как главное действие экрана. Снаружи Telegram (dev в браузере,
 * Playwright) экран рендерит <MainButtonFallback/> — см. useMainButtonFallback.
 */
export function useMainButton({
  text,
  visible = true,
  enabled = true,
  loading = false,
  onClick,
}: MainButtonOptions): void {
  const cb = useRef(onClick);
  useEffect(() => {
    cb.current = onClick;
  });

  useEffect(() => {
    safe(() => {
      if (!mainButton.isMounted()) mainButton.mount();
    });
    const off = safe(() => mainButton.onClick(() => cb.current()));
    return () => {
      off?.();
      safe(() => mainButton.setParams({ isVisible: false }));
    };
  }, []);

  useEffect(() => {
    safe(() =>
      mainButton.setParams({
        text,
        isVisible: visible,
        isEnabled: enabled && !loading,
        isLoaderVisible: loading,
      }),
    );
  }, [text, visible, enabled, loading]);
}

/** Показывать ли HTML-заменитель MainButton (вне Telegram). */
export function useMainButtonFallback(): boolean {
  return isMockedEnv();
}

/** Подтверждение разрушительного действия: нативный popup, вне Telegram — window.confirm. */
export async function confirmDestructive(message: string, okText: string): Promise<boolean> {
  if (!isMockedEnv()) {
    try {
      if (popup.show.isAvailable()) {
        const id = await popup.show({
          message,
          buttons: [
            { id: 'ok', type: 'destructive', text: okText },
            { id: 'cancel', type: 'cancel' },
          ],
        });
        return id === 'ok';
      }
    } catch {
      /* fallthrough */
    }
  }
  return window.confirm(message);
}

/** Предупреждение при закрытии Mini App с незаполненной формой. */
export function useClosingConfirmation(enabled: boolean): void {
  useEffect(() => {
    safe(() => {
      if (!closingBehavior.isMounted()) closingBehavior.mount();
      if (enabled) closingBehavior.enableConfirmation();
      else closingBehavior.disableConfirmation();
    });
    return () => {
      safe(() => closingBehavior.disableConfirmation());
    };
  }, [enabled]);
}
